import { Component, OnInit, inject, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ApiService } from '../../core/services/api.service';
import { BannerRevision } from '../../shared/models/models';

@Component({
  selector: 'app-audit',
  standalone: false,
  template: `
    <div class="page-wrapper">
      <header class="top-bar">
        <div class="top-bar-content">
          <button mat-icon-button (click)="back()"><mat-icon>arrow_back</mat-icon></button>
          <div>
            <span class="page-title">{{ bannerName }}</span>
            <span class="page-sub">{{ clientName }} · Historial de cambios</span>
          </div>
        </div>
      </header>

      <main class="page-content">
        @if (loading()) {
          <div class="timeline">
            @for (i of skeletons; track i) {
              <div class="timeline-item skeleton"></div>
            }
          </div>
        } @else if (revisions().length === 0) {
          <div class="empty-state">
            <mat-icon>history</mat-icon>
            <p>No hay historial de cambios para este diseño.</p>
          </div>
        } @else {
          <p class="summary">{{ revisions().length }} revisión{{ revisions().length !== 1 ? 'es' : '' }} encontrada{{ revisions().length !== 1 ? 's' : '' }}</p>
          <div class="timeline">
            @for (rev of revisions(); track rev.rev; let last = $last) {
              <div class="timeline-item" [class.last]="last">
                <div class="timeline-line"></div>
                <div class="timeline-dot" [class]="'dot-' + revtypeColor(rev.revtype)"></div>
                <div class="timeline-card">
                  <div class="card-header">
                    <div class="card-meta">
                      <span class="revtype-badge" [class]="'badge-' + revtypeColor(rev.revtype)">
                        {{ revtypeLabel(rev.revtype) }}
                      </span>
                      @if (rev.design_changed) {
                        <span class="design-badge">
                          <mat-icon>brush</mat-icon> Diseño modificado
                        </span>
                      }
                    </div>
                    <div class="card-timestamp">
                      <mat-icon>schedule</mat-icon>
                      {{ rev.timestamp | date:'dd/MM/yyyy HH:mm:ss' }}
                    </div>
                  </div>

                  <div class="card-who">
                    <mat-icon>person</mat-icon>
                    <span class="who-value">{{ rev.updated_by }}</span>
                    <span class="source-chip" [matTooltip]="sourceLabel(rev.source)">{{ rev.source }}</span>
                  </div>

                  @if (rev.changes.length > 0) {
                    <div class="changes-list">
                      <p class="changes-title">Campos modificados</p>
                      @for (change of rev.changes; track change.field) {
                        <div class="change-row">
                          <span class="change-field">{{ change.field }}</span>
                          <div class="change-values">
                            <span class="value-old">{{ change.previous_value ?? '(vacío)' }}</span>
                            <mat-icon class="arrow-icon">arrow_forward</mat-icon>
                            <span class="value-new">{{ change.new_value ?? '(vacío)' }}</span>
                          </div>
                        </div>
                      }
                    </div>
                  } @else if (rev.revtype === 'ADD') {
                    <p class="no-changes">Diseño creado por primera vez.</p>
                  } @else {
                    <p class="no-changes">Sin cambios en los campos registrados.</p>
                  }
                </div>
              </div>
            }
          </div>
        }
      </main>
    </div>
  `,
  styleUrls: ['./audit.component.scss']
})
export class AuditComponent implements OnInit {
  private api = inject(ApiService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);

  revisions = signal<BannerRevision[]>([]);
  loading = signal(true);
  skeletons = Array(4);
  bannerName = '';
  clientName = '';
  clientId = 0;
  bannerId = 0;

  ngOnInit(): void {
    this.bannerId = Number(this.route.snapshot.paramMap.get('bannerId'));
    this.bannerName = history.state?.bannerName ?? `Diseño ${this.bannerId}`;
    this.clientName = history.state?.clientName ?? '';
    this.clientId = history.state?.clientId ?? 0;

    this.api.getBannerRevisions(this.bannerId).subscribe({
      next: revs => { this.revisions.set(revs.reverse()); this.loading.set(false); },
      error: () => this.loading.set(false),
    });
  }

  revtypeLabel(type: string): string {
    return { ADD: 'Creado', MOD: 'Modificado', DEL: 'Eliminado' }[type] ?? type;
  }

  revtypeColor(type: string): string {
    return { ADD: 'green', MOD: 'blue', DEL: 'red' }[type] ?? 'gray';
  }

  sourceLabel(source: string): string {
    const map: Record<string, string> = {
      'http:user': 'Usuario',
      'http:role_app': 'App externa',
      'job:scheduled-banners': 'Job automático',
      'job:freemium-banners': 'Job freemium',
      'job:recurrent-scheduling': 'Job recurrente',
      'job:update-meta-ads-status': 'Job Meta Ads',
      'sqs:meta-ads-result': 'Meta Ads (SQS)',
      'kafka:product-set-deleted': 'Kafka',
      'kafka:products-count-result': 'Kafka',
    };
    return map[source] ?? source;
  }

  back(): void {
    if (this.clientId) {
      this.router.navigate(['/clients', this.clientId, 'banners'], { state: { clientName: this.clientName } });
    } else {
      this.router.navigate(['/clients']);
    }
  }
}
